import { useAuth } from '@agentnet/auth';
import { DataTable, pxToRem } from '@fluentsms/agentnet-web-components';
import { Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { _debounce, ColDef, IRowNode } from 'ag-grid-enterprise';
import { SavePreferredOffices } from 'api/admin/interfaces';
import { getPrefferedOffices, setPrefferedOffices } from 'api/admin/preferred-offices-api';
import clsx from 'clsx';
import { AGENCY_SUPPORT_PHONE, KBLinks } from 'features/constants';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { Notification } from 'ui-kit/components/notification/Notification';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import '../files/files-summary/files-list/FilesList.scss';
import {
  AGENCY_SUPPORT_EMAIL,
  PREFERRED_OFFICE_EMPTY_STATE_AGENT,
  PREFERRED_OFFICE_EMPTY_STATE_FA,
  UserRoleTypes,
} from './constants';
import { AsyncStatus } from './manageUsers/manageUsers';
import AgentPreferredOffice from './Icons/AgentPreferredOffice';
import FAPreferredOffice from './Icons/FAPreferredOffice';

interface PortalApiErrorPayload {
  messages?: string[];
  message?: string;
  errorCode?: number | string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.common.white,
    },
    rootWithGrid: {
      maxHeight: 'calc(100vh - 170px)',
    },
    rootEmptyState: {
      flex: 1,
      minHeight: 'calc(100vh - 115px)',
    },
    contentWrap: {
      padding: pxToRem(0, 0, 32),
    },
    mb: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(3),
      marginLeft: theme.spacing(3),
      width: '926px',
    },
    preferredOfficeDesc: {
      marginLeft: theme.spacing(3),
    },
    mb2: {
      marginBottom: theme.spacing(2),
    },
    userName: {
      marginBottom: theme.spacing(0.5),
    },
    heading: {},
    captionDivider: {
      width: theme.spacing(2),
      display: 'inline-block',
    },
    fieldSection: {},
    fieldSectionLeft: {
      padding: theme.spacing(3),
    },
    fieldSectionRight: {
      padding: theme.spacing(3),
      marginTop: 0,
    },
    adornmentIcon: {
      padding: 0,
      '&:hover': {
        background: 'transparent',
      },
    },
    infoIcon: {
      alignItems: 'flex-start',
      marginRight: theme.spacing(3),
      marginLeft: theme.spacing(3),
      width: 'calc(100% - 48px)',
    },
    ctaBtn: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(3),
      width: '100%',
    },
    alertContainer: {
      marginBottom: '63px',
    },
    alertMsg: {
      width: '-webkit-fill-available',
      position: 'absolute',
      bottom: '63px',
      left: 0,
      padding: '0 35px',
    },
    tableStyles: {
      marginTop: '0px !important',
      marginLeft: theme.spacing(3),
      marginRight: theme.spacing(3),
      height: 'calc(100vh - 415px) !important',
    },
    emptyStatePage: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      minHeight: 0,
      background: theme.palette.common.white,
    },
    emptyStateContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(3),
      textAlign: 'center',
      padding: theme.spacing(0, 3),
    },
    emptyStateText: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      alignItems: 'center',
    },
    emptyStateSupport: {
      whiteSpace: 'normal',
      [theme.breakpoints.up('md')]: {
        whiteSpace: 'nowrap',
      },
    },
    emailLink: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      '&:hover': {
        color: theme.palette.primary.dark,
      },
      '&:focus': {
        color: theme.palette.primary.dark,
      },
    },
  }),
);

const getSelectedJurisdictionIds = (rows: { FirmLocationJurisdictionID: number }[]): number[] =>
  rows.map((row) => row.FirmLocationJurisdictionID).sort((a, b) => a - b);

const areSelectionsEqual = (currentIds: number[], initialIds: number[]): boolean =>
  currentIds.length === initialIds.length && currentIds.every((id, index) => id === initialIds[index]);

const prefferedOfficeContent = () => {
  const classes = useStyles();
  const gridRef = useRef<any>(null);
  const { addSnackbarMessage } = useSnackBars();
  const { addGlobalMsg } = useGlobalMessages();

  const showApiErrorsAsGlobalMsg = useCallback(
    (error: PortalApiErrorPayload) => {
      const errorMessages = error.messages ?? [error.message ?? String(error)];
      errorMessages.forEach((message) => addGlobalMsg({ message, type: 'error' }));
    },
    [addGlobalMsg],
  );
  const [gridApi, setGridApi] = useState<any>(null);
  const { getAccessToken } = useAuth();
  const [rowData, setRowData] = useState<any>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { setpreferredOffice, profile } = profileCtx;
  const isFAAgent = profile?.roleTypeId === UserRoleTypes.Agent;
  const PreferredOfficeIcon = isFAAgent ? AgentPreferredOffice : FAPreferredOffice;
  const preferredOfficesData = async () => {
    const token = await getAccessToken();
    const prefferedOfficeresponse = await getPrefferedOffices(token);
    //add isSelected property directly to the row data
    const processedData = (prefferedOfficeresponse ?? []).map((row: any) => ({
      ...row,
      isSelected: row.IsSelected > 0,
    }));
    setRowData(processedData);
  };

  const initialSelectedIds = useMemo(
    () => getSelectedJurisdictionIds(rowData.filter((row: { IsSelected: number }) => row.IsSelected > 0)),
    [rowData],
  );

  const currentSelectedIds = useMemo(() => getSelectedJurisdictionIds(selectedRows ?? []), [selectedRows]);

  const hasUnsavedChanges = useMemo(
    () => rowData.length > 0 && !areSelectionsEqual(currentSelectedIds, initialSelectedIds),
    [rowData.length, currentSelectedIds, initialSelectedIds],
  );

  useEffect(() => {
    if (gridApi) {
      rowData.forEach((row: { IsSelected: number }, index: number) => {
        if (row.IsSelected > 0) {
          gridApi.forEachNode((node: IRowNode) => {
            if (node.rowIndex === index) {
              node.setSelected(true);
            }
          });
        }
      });
      setSelectedRows(gridApi.getSelectedRows());
    }
  }, [gridApi, rowData]);
  const { execute: executePreferredOfficeData, status: getPreferredOfficeData } = useAsync(preferredOfficesData, false);
  const actionStatus = getPreferredOfficeData === AsyncStatus.PENDING ? AsyncStatus.PENDING : AsyncStatus.IDLE;
  const isRequestComplete =
    getPreferredOfficeData === AsyncStatus.SUCCESS || getPreferredOfficeData === AsyncStatus.ERROR;
  const hasOffices = rowData.length > 0;
  const showEmptyState = isRequestComplete && !hasOffices;

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    //select preselected rows more efficiently
    const selectedNodes = params.api.getRenderedNodes().filter((node: any) => node.data?.isSelected);
    selectedNodes.forEach((node: any) => node.setSelected(true));
    setSelectedRows(params.api.getSelectedRows());
  };

  const resetToInitialSelection = useCallback(() => {
    if (!gridApi) {
      return;
    }
    gridApi.deselectAll();
    gridApi.forEachNode((node: IRowNode) => {
      if (node.data?.IsSelected > 0) {
        node.setSelected(true);
      }
    });
    setSelectedRows(gridApi.getSelectedRows());
  }, [gridApi]);

  //debounce selection changes
  const debouncedSelectionChanged = useMemo(
    () =>
      _debounce((event: any) => {
        const currentselectedRows = gridRef.current?.api.getSelectedRows();
        setSelectedRows(currentselectedRows);
      }, 100),
    [],
  );

  const savePreferredOffices = async () => {
    try {
      if (selectedRows) {
        const jurisdictionIds: number[] = selectedRows.map((o: any) => o.FirmLocationJurisdictionID);

        if (jurisdictionIds !== undefined) {
          const saveOffices: SavePreferredOffices = {
            FirmLocationJurisdictionIds: jurisdictionIds.join(','),
          };
          const token = await getAccessToken();
          const saveResponse = await setPrefferedOffices(token, saveOffices);
          const prefferedOfficeresponse = await getPrefferedOffices(token);
          if (setpreferredOffice) {
            setpreferredOffice(prefferedOfficeresponse);
          }
          if (saveResponse.Status) {
            addSnackbarMessage({
              message: 'Saving Successful',
              type: 'success',
            });
            // NOTE: window.location.reload() was already in place before this change.
            // The only update here is to run it on successful save only, so failed saves
            // no longer reload the page and the global error message can remain visible.
            // A full reload is still used because refreshing the AG Grid in place has a known
            // checkbox selection sync issue that needs to be fixed before this can be removed.
            window.location.reload();
          }
        }
      }
    } catch (error) {
      showApiErrorsAsGlobalMsg(error as PortalApiErrorPayload);
    }
  };

  const defaultColDef = useMemo<ColDef>(() => {
    // call get the list API here;
    executePreferredOfficeData();
    return {
      flex: 2,
      floatingFilter: true,
      filter: true,
      sortable: true,
      resizable: true,
      editable: false,
      suppressMenu: true,
      filterParams: { closeOnApply: true, suppressAndOrCondition: true },
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellClass: 'ag-cell-normal-height',
    };
  }, []);

  const prefferedOfficeColumnsConfig: ColDef[] = [
    {
      field: '',
      maxWidth: 40,
      filter: false,
      floatingFilter: false,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
    },
    {
      headerName: 'Firm',
      field: 'FirmName',
      tooltipField: 'FirmName',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Office',
      field: 'BusinessName',
      tooltipField: 'BusinessName',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'State',
      maxWidth: 100,
      filter: 'agTextColumnFilter',

      field: 'StateCode',
      tooltipField: 'StateCode',
    },
    {
      headerName: 'Underwriter',
      field: 'UnderwriterName',
      tooltipField: 'UnderwriterName',
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Account',
      maxWidth: 140,
      field: 'FirmLocationJurisdictionID',
      tooltipField: 'FirmLocationJurisdictionID',
      filter: 'agTextColumnFilter',
    },

    {
      headerName: 'ALTA ID',
      maxWidth: 140,
      // tooltipField: 'altaid',
      filter: 'agTextColumnFilter',
      field: 'ALTAID',
      tooltipField: 'ALTAID',
    },
  ];

  return (
    <>
      <div
        className={clsx(classes.root, {
          [classes.rootEmptyState]: showEmptyState,
          [classes.rootWithGrid]: hasOffices,
        })}
      >
        <LoadingSpinner status={actionStatus} variant="linear" />
        {isRequestComplete && (
          <PageHeader
            title="Preferred Offices"
            subtitle="Customize your file creation workflow by selecting which offices should appear in the office selection dropdown menu. This personalized filter helps streamline file creation by displaying only your chosen offices within the active firm."
            menuItems={[
              {
                label: 'Knowledge Base',
                link: KBLinks.prefferedOffice,
              },
            ]}
          />
        )}

        {showEmptyState && (
          <div className={classes.emptyStatePage}>
            <div className={classes.emptyStateContainer}>
              <PreferredOfficeIcon />
              <div className={classes.emptyStateText}>
                <Typography variant="h3">
                  {isFAAgent ? PREFERRED_OFFICE_EMPTY_STATE_FA.title : PREFERRED_OFFICE_EMPTY_STATE_AGENT.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {isFAAgent
                    ? PREFERRED_OFFICE_EMPTY_STATE_FA.description
                    : PREFERRED_OFFICE_EMPTY_STATE_AGENT.description}
                </Typography>
                {isFAAgent && (
                  <Typography variant="body2" color="textSecondary" className={classes.emptyStateSupport}>
                    {PREFERRED_OFFICE_EMPTY_STATE_FA.supportTextPrefix} {AGENCY_SUPPORT_PHONE}{' '}
                    {PREFERRED_OFFICE_EMPTY_STATE_FA.supportTextMiddle}{' '}
                    <a href={`mailto:${AGENCY_SUPPORT_EMAIL}`} className={classes.emailLink}>
                      {AGENCY_SUPPORT_EMAIL}
                    </a>{' '}
                    {PREFERRED_OFFICE_EMPTY_STATE_FA.supportTextSuffix}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        )}

        {hasOffices && (
          <>
            <div className={classes.mb2}>
              <Grid item sm={12}>
                <Notification inline severity="info" className={classes.infoIcon}>
                  If multiple offices work on the same file, it is recommended to select all offices to avoid file
                  duplication.
                </Notification>
              </Grid>
            </div>
            <div className={clsx('ag-theme-alpine table-grid', classes.tableStyles)}>
              <DataTable
                columnDefs={prefferedOfficeColumnsConfig}
                ref={gridRef}
                rowData={rowData}
                onGridReady={onGridReady}
                defaultColDef={defaultColDef}
                gridOptions={{
                  suppressRowClickSelection: true,
                  rowSelection: 'multiple',
                  //enable virtualization
                  rowBuffer: 10, //reduce from default
                  rowModelType: 'clientSide',
                  enableCellTextSelection: false,
                  suppressMovableColumns: true,
                  //reduce DOM elements, improve scrolling and disable anything slowing it down
                  suppressColumnVirtualisation: false,
                  suppressAnimationFrame: true,
                  suppressColumnMoveAnimation: true,
                  suppressAggFuncInHeader: true,
                  suppressLoadingOverlay: true,
                  suppressFieldDotNotation: true,
                  suppressRowDeselection: true,
                }}
                onSelectionChanged={debouncedSelectionChanged}
              />
            </div>
          </>
        )}

        {hasUnsavedChanges && (
          <BottomAppBar
            primaryActionLabel="Save"
            primaryActionDataQa="preferred-offices-button-save"
            onPrimaryClick={() => savePreferredOffices()}
            cancelLabel="Clear All"
            cancelDataQa="preferred-offices-button-clear-all"
            onCancel={resetToInitialSelection}
          />
        )}
      </div>
    </>
  );
};

export default prefferedOfficeContent;
